import type {
  CreateTicketInput,
  UpdateTicketInput,
  ChangeTicketStatusInput,
  AssignTicketInput,
  AddTicketCommentInput,
  EscalateTicketInput,
  ListTicketsQuery,
  CreateSupportCategoryInput,
  UpdateSupportCategoryInput,
  PaginatedResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Response types ──────────────────────────────────────────────────────────

export interface SupportCategory {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SlaPolicy {
  id: string;
  name: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  response_minutes: number;
  resolution_minutes: number;
  is_active: boolean;
}

export interface TicketUser {
  id: string;
  name: string;
}

export interface TicketListItem {
  id: string;
  ticket_number: string;
  title: string;
  status: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  is_overdue: boolean;
  sla_due_at: string | null;
  created_at: string;
  updated_at: string;
  category: { id: string; name: string } | null;
  created_by: TicketUser;
  assigned_to: TicketUser | null;
}

export interface TicketComment {
  id: string;
  comment_text: string;
  is_internal: boolean;
  commented_by: TicketUser;
  created_at: string;
  updated_at: string;
}

export interface TicketAttachment {
  id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: TicketUser;
  created_at: string;
}

export interface TicketAuditEntry {
  id: string;
  action: string;
  actor: TicketUser;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
}

export interface TicketDetail extends TicketListItem {
  description: string | null;
  department: { id: string; name: string } | null;
  sla_policy: SlaPolicy | null;
  escalation_level: number;
  resolved_at: string | null;
  closed_at: string | null;
  comments: TicketComment[];
  attachments: TicketAttachment[];
  audit_log: TicketAuditEntry[];
}

export interface TechSupportDashboard {
  open: number;
  in_progress: number;
  waiting: number;
  resolved_today: number;
  overdue: number;
  avg_resolution_hours: number;
  by_priority: { priority: string; count: number }[];
  by_category: { category_id: string; category_name: string; count: number }[];
}

// ── Service ─────────────────────────────────────────────────────────────────

export const techSupportService = {
  listTickets: (query?: Partial<ListTicketsQuery>) =>
    api.get<PaginatedResponse<TicketListItem>>('/tech-support/tickets', { params: query }),

  getTicket: (id: string) =>
    api.get<{ data: TicketDetail }>(`/tech-support/tickets/${id}`),

  createTicket: (input: CreateTicketInput) =>
    api.post<{ data: { id: string; ticket_number: string } }>('/tech-support/tickets', input),

  updateTicket: (id: string, input: UpdateTicketInput) =>
    api.patch(`/tech-support/tickets/${id}`, input),

  changeStatus: (id: string, input: ChangeTicketStatusInput) =>
    api.patch(`/tech-support/tickets/${id}/status`, input),

  assignTicket: (id: string, input: AssignTicketInput) =>
    api.patch(`/tech-support/tickets/${id}/assign`, input),

  addComment: (ticketId: string, input: AddTicketCommentInput) =>
    api.post<{ data: { id: string } }>(`/tech-support/tickets/${ticketId}/comments`, input),

  deleteComment: (ticketId: string, commentId: string) =>
    api.delete(`/tech-support/tickets/${ticketId}/comments/${commentId}`),

  escalateTicket: (id: string, input: EscalateTicketInput) =>
    api.post(`/tech-support/tickets/${id}/escalate`, input),

  deleteTicket: (id: string) =>
    api.delete(`/tech-support/tickets/${id}`),

  listCategories: () =>
    api.get<{ data: SupportCategory[] }>('/tech-support/categories'),

  listAllCategories: () =>
    api.get<{ data: SupportCategory[] }>('/tech-support/categories?activeOnly=false'),

  createCategory: (input: CreateSupportCategoryInput) =>
    api.post<{ data: { id: string } }>('/tech-support/categories', input),

  updateCategory: (id: string, input: UpdateSupportCategoryInput) =>
    api.patch(`/tech-support/categories/${id}`, input),

  deleteCategory: (id: string) =>
    api.delete(`/tech-support/categories/${id}`),

  listSlaPolicies: () =>
    api.get<{ data: SlaPolicy[] }>('/tech-support/sla-policies'),

  getDashboard: () =>
    api.get<{ data: TechSupportDashboard }>('/tech-support/dashboard'),
};
